"use client";

import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { api } from "@/lib/api";
import { isSlowConnection } from "@/utils/network.utils";
import { changePostHogIdentity, startPostHog } from "@/utils/analytics.utils";
import { runWhenIdle } from "@/utils/idle.utils";
import { resolvePostHogIdentifyAction } from "./postHogIdentify.utils";
const POSTHOG_START_TIMEOUT_MS = 5_000;
function PostHogPageviewTracker({
  onRouteChange
}: {
  onRouteChange: (url: string) => void;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  useEffect(() => {
    const search = searchParams.toString();
    const currentUrl = window.location.origin + pathname + (search ? `?${search}` : "");
    onRouteChange(currentUrl);
  }, [pathname, searchParams, onRouteChange]);
  return null;
}
export function PostHogProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user,
    isLoading,
    session
  } = useCurrentUser();
  const {
    data: org,
    isLoading: isOrgLoading
  } = api.org.getMyOrg.useQuery(undefined, {
    enabled: session !== null // boolean-naming-exempt: external contract — react-query option
  });
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const lastCapturedUrl = useRef<string | null>(null);
  const handleRouteChange = useCallback((url: string) => setCurrentUrl(url), []);
  useEffect(() => {
    const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;
    if (!posthogKey || !posthogHost) return;
    return runWhenIdle(() => {
      const isOnSlowLink = isSlowConnection();
      void startPostHog({
        apiKey: posthogKey,
        config: {
          disable_session_recording: isOnSlowLink,
          enable_heatmaps: !isOnSlowLink,
          // boolean-naming-exempt: external contract — posthog-js option
          api_host: posthogHost,
          person_profiles: "identified_only",
          capture_pageview: false,
          // boolean-naming-exempt: external contract — posthog-js option
          capture_pageleave: true,
          // boolean-naming-exempt: external contract — posthog-js option
          autocapture: true,
          // boolean-naming-exempt: external contract — posthog-js option
          capture_performance: true,
          // boolean-naming-exempt: external contract — posthog-js option
          capture_exceptions: false,
          // boolean-naming-exempt: external contract — posthog-js option
          defaults: "2025-05-24",
          session_recording: {
            maskAllInputs: false,
            // boolean-naming-exempt: external contract — posthog-js option
            maskInputOptions: {
              password: true // boolean-naming-exempt: external contract — posthog-js option
            },
            recordCrossOriginIframes: false,
            // boolean-naming-exempt: external contract — posthog-js option
            recordHeaders: false,
            // boolean-naming-exempt: external contract — posthog-js session_recording option
            recordBody: false // boolean-naming-exempt: external contract — posthog-js session_recording option
          }
        }
      });
    }, {
      timeoutMs: POSTHOG_START_TIMEOUT_MS
    });
  }, []);
  useEffect(() => {
    const actions = resolvePostHogIdentifyAction({
      user,
      isLoading,
      org: org ? {
        id: org.id,
        name: org.name
      } : undefined
    });
    actions.forEach(action => {
      if (action.type === "identify") {
        changePostHogIdentity(client => client.identify(action.distinctId, action.properties));
      } else if (action.type === "group") {
        changePostHogIdentity(client => client.group(action.groupType, action.groupKey, action.properties));
      }
    });
  }, [user, isLoading, org, isOrgLoading]);
  useEffect(() => {
    const isIdentityReady = !isLoading && !isOrgLoading;
    if (!isIdentityReady || !currentUrl) return;
    if (lastCapturedUrl.current === currentUrl) return;
    lastCapturedUrl.current = currentUrl;
    changePostHogIdentity(client => client.capture("$pageview", {
      $current_url: currentUrl
    }));
  }, [currentUrl, isLoading, isOrgLoading]);
  return <>
            <Suspense fallback={null} data-sentry-element="Suspense" data-sentry-source-file="PostHogProvider.tsx">
                <PostHogPageviewTracker onRouteChange={handleRouteChange} data-sentry-element="PostHogPageviewTracker" data-sentry-source-file="PostHogProvider.tsx" />
            </Suspense>
            {children}
        </>;
}